import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsTabletOrMobile } from "@/hooks/use-tablet-mobile";
import { useLocation } from "react-router-dom";
import TopNav from "@/components/layout/TopNav";
import Sidebar from "@/components/Sidebar";
import { SidebarProvider } from "@/contexts/SidebarContext";
interface AppLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
}
const AppLayout = ({
  children,
  pageTitle = "StudiBudi"
}: AppLayoutProps) => {
  const isMobile = useIsMobile();
  const isTabletOrMobile = useIsTabletOrMobile();
  const location = useLocation();

  // Determine if we're on tablet (between mobile and desktop)
  const isTablet = isTabletOrMobile && !isMobile;

  // Initialize sidebar state from sessionStorage, defaulting to collapsed
  // Persists during session but resets on browser refresh/new tab
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (isTabletOrMobile) return true;
    
    const savedState = sessionStorage.getItem('sidebar-collapsed-state');
    return savedState !== null ? savedState === 'true' : true;
  });

  // Save sidebar state to sessionStorage whenever it changes (desktop only)
  useEffect(() => {
    if (!isTabletOrMobile) {
      sessionStorage.setItem('sidebar-collapsed-state', String(isSidebarCollapsed));
    }
  }, [isSidebarCollapsed, isTabletOrMobile]);

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Handle sidebar toggle for mobile/tablet - use overlay instead of pushing content
  const handleSidebarToggle = () => {
    if (isTabletOrMobile) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  // Check if we're on a topic page
  const isTopicPage = location.pathname.startsWith('/topic');

  // Mobile and tablet layout with TopNav and overlay sidebar
  if (isTabletOrMobile) {
    return (
      <SidebarProvider value={{ toggleSidebar: handleSidebarToggle, isSidebarCollapsed: !isMobileSidebarOpen }}>
        <div className="fixed inset-0 flex overflow-hidden bg-background">
          {/* Sidebar overlay for mobile/tablet */}
          <>
            {/* Overlay backdrop - covers entire viewport */}
            {isMobileSidebarOpen && (
              <div 
                className="fixed inset-0 bg-black/50 z-[60] animate-fade-in"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
            )}
            {/* Sidebar overlay - slides in from left */}
            <div className={`fixed left-0 top-0 h-full w-72 z-[70] transition-transform duration-300 ease-out ${
              isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
              <Sidebar 
                onToggle={() => setIsMobileSidebarOpen(false)} 
                showHeader={false} 
                collapsed={false}
                onClose={() => setIsMobileSidebarOpen(false)}
              />
            </div>
          </>
          
          {/* Main content area with top nav */}
          <div className="flex-1 flex flex-col min-w-0 bg-app-background">
            {/* Top Navigation for mobile/tablet - hidden on topic pages */}
            {/* {!isTopicPage && (
              <TopNav 
                onToggleSidebar={handleSidebarToggle}
                isSidebarCollapsed={!isMobileSidebarOpen} 
              />
            )} */}
            
            {/* Main content */}
            <div className="flex-1 flex flex-col min-h-0 bg-transparent overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Desktop layout with sidebar that pushes content
  return (
    <SidebarProvider value={{ toggleSidebar: handleSidebarToggle, isSidebarCollapsed }}>
      <div className="fixed inset-0 flex overflow-hidden bg-background">
        {/* Desktop sidebar - pushes content */}
        <div className={`flex-shrink-0 transition-all duration-300 ${isSidebarCollapsed ? 'w-16' : 'w-72'} overflow-hidden`}>
          <Sidebar onToggle={setIsSidebarCollapsed} showHeader={false} collapsed={isSidebarCollapsed} />
        </div>
        
        {/* Main content area with top nav */}
        <div className="flex-1 flex flex-col min-w-0 bg-app-background">
          {/* Top Navigation - hidden on topic pages */}
          {/* {!isTopicPage && (
            <TopNav 
              onToggleSidebar={handleSidebarToggle}
              isSidebarCollapsed={isSidebarCollapsed} 
            />
          )} */}
          
          {/* Main content */}
          <div className="flex-1 flex flex-col min-h-0 bg-transparent overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};
export default AppLayout;